/**
 * Domain errors for the device enrollment flow.
 * Design: enroll-device-on-login §7 (Error mapping)
 *
 * These are pure domain objects — ZERO external dependencies.
 */

/**
 * Thrown when the backend signals the device is already registered.
 * Maps to HTTP 400 (with 'already enrolled' body), 204, or other 4xx from
 * POST /v1/clock/app/enroll/device.
 * Banner A: "Este dispositivo ya está registrado"
 */
export class DeviceAlreadyRegisteredError extends Error {
  override readonly name = 'DeviceAlreadyRegisteredError';

  constructor(message = 'Este dispositivo ya está registrado') {
    super(message);
  }
}

/**
 * Thrown when enrollment fails due to network issues or 5xx server errors.
 * Banner B: "No pudimos registrar el dispositivo"
 */
export class DeviceEnrollmentFailedError extends Error {
  override readonly name = 'DeviceEnrollmentFailedError';

  constructor(message = 'No pudimos registrar el dispositivo. Contactá soporte.') {
    super(message);
  }
}

/**
 * Thrown when the backend signals that the clock license limit has been exceeded.
 * Maps to HTTP 400 with body { status: 400, message: { 498: "LICENSE_CLOCK_EXCEEDED" } }
 * from POST /v1/clock/app/enroll/device.
 * Banner C: "No pudimos registrar el dispositivo. Contactá soporte por error en licencia Clock excedida."
 */
export class LicenseExceededError extends Error {
  override readonly name = 'LicenseExceededError';

  constructor(message = 'Licencia Clock excedida') {
    super(message);
  }
}

/**
 * Thrown when the backend signals the organization has no Clock license at all.
 * Maps to HTTP 400 with body { status: 400, message: { 499: "LICENSE_CLOCK_NOT_FOUND" } }.
 */
export class LicenseNotFoundError extends Error {
  override readonly name = 'LicenseNotFoundError';

  constructor(message = 'Licencia Clock no encontrada') {
    super(message);
  }
}

/**
 * Thrown when the backend rejects the device's timezone during enrollment.
 * Maps to HTTP validation error { status: '110', message: [{ timezone_pg: 'if118' }] }
 * from POST /v1/clock/app/enroll/device. Carries the rejected value so the UI
 * can surface it (e.g. "your timezone (America/Cordoba) isn't valid").
 */
export class TimezoneRejectedError extends Error {
  override readonly name = 'TimezoneRejectedError';
  readonly timezone: string;

  constructor(timezone: string) {
    super(`El backend rechazó el huso horario "${timezone}"`);
    this.timezone = timezone;
  }
}
